import { isArray } from 'lodash-es';

// 内容类型枚举，与后端保持一致
export enum ContentType {
  TEXT = 'TEXT',
  ECHARTS = 'ECHARTS',
  IMAGE = 'IMAGE',
  EXCEL = 'EXCEL',
  WORD = 'WORD',
  PDF = 'PDF',
  FILE = 'FILE',
  MARKDOWN = 'MARKDOWN',
  TABLE = 'TABLE',
  NAVIGATION = 'NAVIGATION',
}

// 消息部分类型定义
export interface MessagePart {
  type:
    | 'text'
    | 'chart'
    | 'error'
    | 'table'
    | 'excel'
    | 'word'
    | 'pdf'
    | 'file'
    | 'image'
    | 'markdown'
    | 'thinking'
    | 'thinkingBlock'
    | 'thinkingTimeline'
    | 'toolStatus';
  data?: any;
  blockId?: string;
  thinkingTime?: number; // 每轮思考独立的时间（秒）
  replying?: boolean; // 当前片段是否仍在流式生成
}

// 结构化输出类型定义
export interface StructuredOutput {
  blockId: string;
  contentType: ContentType;
  data: any;
  metadata?: any;
}

// RENDER标记正则：匹配 {{RENDER:blockId}}
const RENDER_MARKER_REGEX = /\{\{RENDER:([^}]+)\}\}/g;

/**
 * 处理文本中的RENDER标记，替换为对应的结构化内容
 * @param text 包含RENDER标记的文本
 * @param cache 结构化输出缓存
 * @returns MessagePart数组
 */
export function processRenderMarkers(text: string, cache: Map<string, StructuredOutput>): MessagePart[] {
  if (!text) {
    return [];
  }

  const parts: MessagePart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // 重置正则的lastIndex
  RENDER_MARKER_REGEX.lastIndex = 0;
  while ((match = RENDER_MARKER_REGEX.exec(text)) !== null) {
    // 添加RENDER标记之前的文本
    if (match.index > lastIndex) {
      const textBefore = text.slice(lastIndex, match.index);
      if (textBefore.trim()) {
        parts.push({ type: 'text', data: textBefore });
      }
    }

    // 提取blockId
    const blockId = match[1];
    const cachedOutput = cache.get(blockId);
    if (cachedOutput) {
      // 根据contentType创建对应的MessagePart
      const part = convertStructuredOutputToPart(cachedOutput);
      if (part) parts.push(part);
      console.log(`RENDER标记替换: blockId=${blockId}, contentType=${cachedOutput.contentType}`);
    } else {
      // 缓存中没有数据，显示loading占位符
      parts.push({ type: 'text', data: `正在加载内容(${blockId})...` });
      console.log(`RENDER标记未找到缓存: blockId=${blockId}`);
    }

    lastIndex = match.index + match[0].length;
  }

  // 添加最后一个RENDER标记之后的文本
  if (lastIndex < text.length) {
    const textAfter = text.slice(lastIndex);
    if (textAfter.trim()) {
      parts.push({ type: 'text', data: textAfter });
    }
  }

  // 如果没有RENDER标记，直接返回文本
  if (parts.length === 0 && lastIndex === 0 && text.trim()) {
    parts.push({ type: 'text', data: text });
  }

  return parts;
}

/**
 * 将结构化输出转换为MessagePart
 */
function convertStructuredOutputToPart(output: StructuredOutput): MessagePart | null {
  switch (output.contentType) {
    case ContentType.ECHARTS:
      // ECharts图表配置
      let chartOption = output.data;
      if (typeof output.data === 'string') {
        try {
          chartOption = JSON.parse(output.data);
        } catch (e) {
          console.error('ECharts配置解析失败:', e);
        }
      }
      return { type: 'chart', data: chartOption, blockId: output.blockId };

    case ContentType.TABLE:
      return { type: 'table', data: output.data, blockId: output.blockId };

    case ContentType.NAVIGATION:
      // 专属协议由智能体回答层消费，不转换为通用消息片段。
      return null;

    case ContentType.EXCEL:
      // Excel文件，提供下载链接
      return {
        type: 'excel',
        data: output.data,
        blockId: output.blockId,
      };
    case ContentType.WORD:
      // Word文件，提供下载链接
      return {
        type: 'word',
        data: output.data,
        blockId: output.blockId,
      };
    case ContentType.PDF:
      // PDF文件，提供下载链接
      return {
        type: 'pdf',
        data: output.data,
        blockId: output.blockId,
      };
    case ContentType.FILE:
      // 普通文件，提供下载链接
      return {
        type: 'file',
        data: output.data,
        blockId: output.blockId,
      };

    case ContentType.IMAGE:
      return { type: 'image', data: output.data, blockId: output.blockId };

    case ContentType.MARKDOWN:
      return { type: 'markdown', data: output.data, blockId: output.blockId };

    case ContentType.TEXT:
      return { type: 'text', data: output.data, blockId: output.blockId };

    default:
      console.warn(`未知的contentType: ${output.contentType}`);
      return { type: 'text', data: JSON.stringify(output.data), blockId: output.blockId };
  }
}

/**
 * 解析历史消息数据
 * 支持多种格式：
 * 1. JSON 数组格式 [{type: 'log', data: '...'}, ...]
 * 2. JSON 对象格式 {type: 'log', data: '...'}
 * 3. 普通字符串格式
 */
export function parseMessageEvent(options: ParseOptions | { data: string }): MessagePart[] {
  const data = options.data;

  if (!data) {
    return [{ type: 'text', data: '' }];
  }

  let messages: any;
  let parts: MessagePart[] = [];

  // 如果是普通字符串，直接返回
  if (typeof data === 'string' && !data.startsWith('{') && !data.startsWith('[')) {
    return [{ type: 'text', data }];
  }

  try {
    messages = JSON.parse(data);
  } catch (e) {
    // 解析失败，返回原始文本
    return [{ type: 'text', data }];
  }

  // 如果解析结果不是数组，转换为数组
  if (!isArray(messages)) {
    if (typeof messages === 'object' && messages !== null) {
      messages = [messages];
    } else {
      return [{ type: 'text', data: String(messages) }];
    }
  }

  if (messages.length === 0) {
    return [{ type: 'text', data: '' }];
  }

  messages.forEach((parsed: any) => {
    if (!parsed || typeof parsed !== 'object') {
      parts.push({ type: 'text', data: String(parsed) });
      return;
    }

    switch (parsed.type) {
      case 'log':
      case 'TEXT':
        parts.push({ type: 'text', data: parsed.data ?? parsed.content ?? '' });
        break;

      case 'chart':
      case 'ECHARTS':
        const chartData = parsed.data ?? parsed;
        if (chartData.type === 'table' || parsed.type === 'TABLE') {
          parts.push({ type: 'table', data: chartData.option ?? chartData.data ?? chartData });
        } else {
          parts.push({ type: 'chart', data: chartData.option ?? chartData.data ?? chartData });
        }
        break;

      case 'error':
        parts.push({ type: 'error', data: parsed.data ?? '请求发生错误' });
        break;

      case 'excel':
      case 'EXCEL':
        parts.push({ type: 'excel', data: parsed.data ?? parsed, blockId: parsed.blockId });
        break;

      case 'image':
      case 'IMAGE':
        parts.push({ type: 'image', data: parsed.data ?? parsed, blockId: parsed.blockId });
        break;

      case 'markdown':
      case 'MARKDOWN':
        parts.push({ type: 'markdown', data: parsed.data ?? parsed.content ?? '' });
        break;

      default:
        // 尝试从其他字段获取数据
        if (parsed.content) {
          parts.push({ type: 'text', data: parsed.content });
        } else if (parsed.data) {
          parts.push({ type: 'text', data: parsed.data });
        } else if (parsed.message) {
          parts.push({ type: 'text', data: parsed.message });
        } else {
          parts.push({ type: 'text', data: JSON.stringify(parsed) });
        }
        break;
    }
  });
  return parts;
}
